/**
 * The signed-in shell.
 *
 * A soft-gray rail on the left, a hairline-separated top bar, and a white
 * content well. `DESIGN.md` keeps dark surfaces scarce — the footer and one
 * featured card — so the navigation here earns its separation from the content
 * through tone and a 1px line rather than through a colour block. The active
 * nav item is the `category-tab-active` treatment at rail scale: a white pill
 * lifted out of the soft ground.
 *
 * Below the `rail` breakpoint the sidebar leaves the flow and slides in over a
 * scrim, rather than the nav squeezing the content well. That is why the rail
 * is written mobile-first here: `fixed` and translated off-screen by default,
 * `rail:` putting it back in the flow.
 */

import { useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { useAuth } from "../auth";
import { keys } from "../queries";
import { SHOW_SCHEDULING } from "../features";
import { Avatar, IconButton, cx } from "./ui";
import {
  ChevronRightIcon,
  DictionaryIcon,
  MenuIcon,
  DashboardIcon,
  SettingsIcon,
  SignOutIcon,
  SourcesIcon,
  TransferIcon,
} from "./icons";

const NAV_ITEMS = [
  // First, and the home page, because it is the one screen written for everyone
  // on the team: what is running and how it went, without needing to know how
  // a source is built. `end` so it is not lit on every other screen too.
  { to: "/", label: "Dashboard", icon: DashboardIcon, end: true },
  // Not `end`, so the entry stays lit on a source's own screens beneath it.
  { to: "/sources", label: "Sources", icon: SourcesIcon, end: false },
  // Its own entry rather than a mode inside Sources: a Transfer Portal Profile
  // has no Feed, no Binding and no ranking method, so the Add Source form is
  // mostly questions that do not apply to one.
  { to: "/transfer-portal", label: "Transfer Portal", icon: TransferIcon, end: false },
  { to: "/stat-dictionary", label: "Stat Dictionary", icon: DictionaryIcon, end: false },
  { to: "/settings", label: "Settings", icon: SettingsIcon, end: false },
];

/**
 * Whether anything in this workspace runs without somebody pressing a button.
 *
 * The bar used to read "Manual runs only" unconditionally, which stopped being
 * true the moment schedules existed — and a chrome element that quietly lies is
 * worse than one that says nothing. Shares the Sources query's cache, so it
 * costs no extra request on the screen where that list is already loaded.
 */
function ScheduleSummary() {
  const { data } = useQuery({ queryKey: keys.profiles, queryFn: api.listProfiles });
  if (!data) return null;

  const scheduled = data.filter((profile) => profile.schedule?.enabled).length;
  return (
    <span
      className="shrink-0 whitespace-nowrap text-caption text-muted-soft"
      title={
        scheduled === 0
          ? "Every Run is started by hand. You can put a source on a schedule once a dry run has passed."
          : "Schedules run inside the portal process, in UTC. The portal misses a firing time that passes while it is not running, and never catches it up."
      }
    >
      {scheduled === 0
        ? "Manual runs only"
        : `${scheduled} source${scheduled === 1 ? "" : "s"} scheduled`}
    </span>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

export function Layout({
  children,
  title,
  description,
  breadcrumbs = [],
  actions,
  wide = false,
}: {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  /**
   * Let this screen's content use the window rather than the 1200px cap.
   *
   * `DESIGN.md` sets that cap for *marketing pages* — the section it is in
   * specifies hero bands, 3-up feature grids, pricing tiers and testimonial
   * cards. An eight-column table of six hundred Bindings is none of those, and
   * it inherited the number anyway: measured on the Fields screen at a 1920px
   * window, the table got 1134px and 456px sat empty beside it, while
   * `Two Point Conversions` was being shown in 145px of input.
   *
   * `main` is not centred, so the cap does not halve the slack — it puts all of
   * it on the right, which is what the screen was reported for.
   *
   * Opt-in rather than lifted for everyone: a screen of prose or of cards reads
   * better at a readable measure, and the page description keeps its own
   * `72ch` either way, so widening here never widens a paragraph.
   */
  wide?: boolean;
}) {
  const { user, signOut } = useAuth();
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-canvas">
      {navOpen && (
        <button
          className="fixed inset-0 z-30 cursor-pointer border-none bg-surface-dark opacity-40 rail:hidden"
          aria-label="Close the menu"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        data-open={navOpen}
        className={cx(
          "flex w-sidebar shrink-0 flex-col border-r border-hairline bg-surface-soft p-sm",
          "fixed inset-y-0 left-0 z-40 -translate-x-full shadow-overlay transition-transform duration-[180ms] ease-portal",
          "data-[open=true]:translate-x-0",
          // `sticky` rather than `static`: the rail stays in the flow, so the
          // content well still sits beside it rather than under it, but it no
          // longer scrolls away with the page. `h-screen` is what makes that
          // work — a flex child left to stretch is as tall as the whole
          // document, and a sticky box the height of its scroll container
          // never moves. It also gives `mt-auto` below a viewport to push
          // against, which is what keeps Sign out reachable without scrolling
          // to the foot of a long screen.
          "rail:sticky rail:inset-y-auto rail:left-auto rail:top-0 rail:h-screen",
          "rail:translate-x-0 rail:shadow-none rail:transition-none",
        )}
      >
        <Wordmark />

        {/* Scrolls on its own if the sections ever outgrow the rail, so a long
            list pushes itself rather than the account card off the bottom. */}
        <nav aria-label="Sections" className="flex min-h-0 flex-1 flex-col gap-xxs overflow-y-auto py-md">
          <div className="px-sm pb-xs text-overline uppercase text-muted-soft">Workspace</div>
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} className="nav-link" to={to} end={end} onClick={() => setNavOpen(false)}>
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto">
          <div className="flex items-center gap-sm rounded-lg border border-hairline bg-canvas p-sm shadow-subtle">
            <Avatar name={user?.display_name ?? "?"} size="size-[32px]" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-body-sm-medium text-ink">{user?.display_name}</div>
              <div className="truncate text-caption font-normal text-muted-soft">{user?.email}</div>
            </div>
            <IconButton
              icon={<SignOutIcon size={16} />}
              label="Sign out"
              onClick={() => void signOut()}
              className="size-[28px] border-none bg-transparent"
            />
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-topbar shrink-0 items-center justify-between gap-md border-b border-hairline bg-canvas px-xl">
          <div className="flex min-w-0 items-center gap-sm">
            <button
              className="icon-btn rail:hidden"
              type="button"
              aria-label="Open the menu"
              onClick={() => setNavOpen(true)}
            >
              <MenuIcon size={18} />
            </button>
            <Breadcrumbs crumbs={breadcrumbs} />
          </div>

          {SHOW_SCHEDULING && <ScheduleSummary />}
        </header>

        <main
          className={cx(
            "w-full flex-1 px-xl pb-xxl pt-xl",
            wide ? "max-w-none" : "max-w-content",
          )}
        >
          {(title || actions) && (
            <div className="mb-lg flex flex-wrap items-start justify-between gap-md">
              <div className="min-w-0">
                {title && <h1 className="m-0 font-display text-display-sm">{title}</h1>}
                {description && (
                  <p className="mb-0 mt-xs max-w-[72ch] text-body-md text-muted">{description}</p>
                )}
              </div>
              {actions && <div className="flex shrink-0 items-center gap-xs">{actions}</div>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  if (crumbs.length === 0) return <span />;

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-xxs overflow-hidden">
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;
        return (
          <span key={`${crumb.label}-${index}`} className="flex items-center gap-xxs">
            {index > 0 && <ChevronRightIcon size={14} className="shrink-0 text-surface-strong" />}
            {crumb.to && !last ? (
              <Link
                to={crumb.to}
                className="whitespace-nowrap text-nav-link text-muted hover:text-ink"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current={last ? "page" : undefined}
                className={cx(
                  "truncate whitespace-nowrap text-nav-link",
                  last ? "text-ink" : "text-muted",
                )}
              >
                {crumb.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/** The brand lockup: a near-black mark and the name in the display face. */
function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <div className={cx("flex items-center gap-sm", compact ? "p-0" : "p-sm")}>
      <span
        aria-hidden="true"
        className="inline-flex size-icon-button shrink-0 items-center justify-center rounded-md bg-primary font-display text-title-sm tracking-[-0.5px] text-on-primary"
      >
        PR
      </span>
      <div className="min-w-0">
        <div className="font-display text-title-sm tracking-[-0.5px] text-ink">Player Ranking</div>
        <div className="text-caption font-normal leading-[1.2] text-muted-soft">Portal</div>
      </div>
    </div>
  );
}

/**
 * The centred panel the sign-in and sign-up screens sit on. White canvas, one
 * hairline card, one near-black action — the same system the shell uses, with
 * the navigation taken away.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="grid min-h-screen place-items-center bg-surface-soft p-lg">
      <div className="w-full max-w-auth">
        <div className="mb-lg flex justify-center">
          <Wordmark compact />
        </div>

        <div className="rounded-xl border border-hairline bg-canvas p-xl shadow-card">
          <div className="mb-lg">
            <h1 className="m-0 font-display text-display-sm">{title}</h1>
            <p className="mb-0 mt-xxs text-body-sm text-muted">{subtitle}</p>
          </div>
          {children}
        </div>

        {footer && <div className="mt-lg text-center text-body-sm text-muted">{footer}</div>}
      </div>
    </div>
  );
}
